import React from 'react';

/**
 * REGEX PATTERNS
 * g = global (find all)
 * i = case-insensitive (handle HTTPS:// or youtube.com/SHORTs)
 */
const patterns = {
    youtube: /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|music\.youtube\.com\/watch\?v=)([^\s&]+)/gi,

    spotify: /https?:\/\/open\.spotify\.com\/(track|album|playlist)\/([a-zA-Z0-9]+)/gi,

    twitter: /https?:\/\/(?:www\.|x\.com|twitter\.com)\/[^\s]+\/status\/([0-9]+)/gi,

    tiktok: /https?:\/\/(?:www\.)?tiktok\.com\/@[^\s]+\/video\/([0-9]+)/gi,

    // Handles /p/, /reel/, and /reels/
    instagram: /https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|reels)\/([a-zA-Z0-9_-]+)/gi,

    reddit: /https?:\/\/(?:www\.)?reddit\.com\/r\/[a-zA-Z0-9_]+\/comments\/([a-zA-Z0-9]+)/gi,

    facebook: /https?:\/\/(?:www\.)?facebook\.com\/[^/]+\/posts\/[0-9]+/gi,

    soundcloud: /https?:\/\/(?:www\.)?soundcloud\.com\/[^\s]+/gi,
};

// Security sandbox policy for iframes
const IFRAME_SANDBOX = "allow-scripts allow-same-origin allow-presentation allow-popups allow-forms";

export const extractEmbeds = (text) => {
    if (!text) return [];

    const embeds = [];

    Object.entries(patterns).forEach(([platform, regex]) => {
        let match;
        // Reset regex index because of the 'g' flag
        regex.lastIndex = 0;

        while ((match = regex.exec(text)) !== null) {
            embeds.push({
                platform,
                // match[1] is usually the ID, match[2] is Spotify's ID
                id: match[2] || match[1],
                type: match[2] ? match[1] : undefined,
                full: match[0],
            });
        }
    });

    return embeds;
};

export const renderEmbed = (embed, idx) => {
    const { platform, id, type, full } = embed;

    // Unique keying using platform and index
    const key = `embed-${platform}-${idx}`;

    const commonStyle = {
        marginTop: '12px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: 'var(--bubble-friend)' // Placeholder background while loading
    };

    switch (platform) {
        case 'youtube':
            return (
                <iframe
                    key={key}
                    src={`https://www.youtube.com/embed/${id}`}
                    width="100%"
                    height="315"
                    style={commonStyle}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                    sandbox={IFRAME_SANDBOX}
                    title="YouTube Video"
                />
            );

        case 'spotify':
            return (
                <iframe
                    key={key}
                    src={`https://open.spotify.com/embed/${type}/${id}`}
                    width="100%"
                    height="80"
                    style={commonStyle}
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    sandbox={IFRAME_SANDBOX}
                    title="Spotify Player"
                />
            );

        case 'tiktok':
            return (
                <iframe
                    key={key}
                    src={`https://www.tiktok.com/embed/v2/${id}`}
                    width="100%"
                    height="575"
                    style={commonStyle}
                    allowFullScreen
                    loading="lazy"
                    sandbox={IFRAME_SANDBOX}
                    title="TikTok Video"
                />
            );

        case 'soundcloud':
            return (
                <iframe
                    key={key}
                    src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(full)}&color=%23ff5500&auto_play=false`}
                    width="100%"
                    height="166"
                    scrolling="no"
                    style={commonStyle}
                    loading="lazy"
                    sandbox={IFRAME_SANDBOX}
                    title="SoundCloud Player"
                />
            );

        case 'facebook':
            return (
                <iframe
                    key={key}
                    src={`https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(full)}&show_text=true`}
                    width="100%"
                    height="500"
                    style={commonStyle}
                    scrolling="no"
                    allow="encrypted-media"
                    loading="lazy"
                    sandbox={IFRAME_SANDBOX}
                    title="Facebook Post"
                />
            );

        // Fallback for platforms with restricted X-Frame-Options (Twitter, Instagram, Reddit)
        case 'twitter':
        case 'instagram':
        case 'reddit':
            const icon = platform === 'twitter' ? '🐦' : platform === 'instagram' ? '📸' : '🤖';
            return (
                <div key={key} className="embed-link-fallback" style={commonStyle}>
                    <a href={full} target="_blank" rel="noopener noreferrer" className="embed-card">
                        <span className="icon">{icon}</span>
                        <div className="embed-info">
                            <span className="embed-title">View {platform.charAt(0).toUpperCase() + platform.slice(1)} content</span>
                            <span className="embed-url-text">{platform}.com/...</span>
                        </div>
                    </a>
                </div>
            );

        default:
            return null;
    }
};